import React, { useState, useMemo } from 'react';
import { DollarSign, Package, Wifi, Gift, CreditCard, ChevronDown, ChevronRight, ArrowDownCircle, Tv } from 'lucide-react';
import { useProfile } from '../ProfileContext';

const WaterfallSummary = ({ onOptimizeClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showRetailBreakdown, setShowRetailBreakdown] = useState(false);
  
  const {
    subscriptions,
    creditCards,
    memberships,
    bundles, // user_bundles - the bundles the user has subscribed to
    activePerks,
    allocations, // credit_card_allocations
    refData,
    profile,
  } = useProfile();

  // ===== CALCULATE WATERFALL VALUES =====
  const calculations = useMemo(() => {
    console.log('WaterfallSummary calculating with:', { 
      bundles, 
      subscriptions, 
      allocations,
      streamingServices: refData?.streamingServices?.slice(0, 3), // Log first 3 to see structure
    });
    
    // Helper to get retail price for a service
    // Only use reference data prices - do not use fallbacks
    // Users should enter their actual costs during onboarding
    const getServiceRetailPrice = (serviceName) => {
      const serviceRef = refData?.streamingServices?.find(
        s => s.service_name?.toLowerCase() === serviceName?.toLowerCase()
      );
      // Try column names from reference data
      return serviceRef?.base_price_monthly || 
             serviceRef?.base_price || 
             serviceRef?.monthly_price ||
             serviceRef?.retail_price ||
             serviceRef?.price ||
             0;
    };

    // ===== BUILD RETAIL BREAKDOWN for expandable view =====
    const retailBreakdown = [];

    // 2. Bundle analysis - now using user_bundles table
    let bundleCost = 0;
    let bundleRetailTotal = 0;
    let bundleCreditApplied = 0;
    const bundleDetails = [];
    const bundledServiceNames = new Set(); // Track which services are in bundles
    
    // Get bundles from user_bundles
    (bundles || []).forEach(userBundle => {
      // Get bundle reference data - check joined data first, then refData
      const joinedBundleData = userBundle.bundles; // From Supabase join
      const bundleRef = refData?.bundles?.find(b => b.bundle_code === userBundle.bundle_code);
      
      const cost = parseFloat(userBundle.monthly_cost) || 0;
      const creditApplied = parseFloat(userBundle.credit_applied) || 0;
      const actualCost = parseFloat(userBundle.actual_cost) || cost;
      
      bundleCost += cost;
      bundleCreditApplied += creditApplied;
      
      // Get services in this bundle - from joined data, refData, or subscriptions
      const bundleServices = joinedBundleData?.bundle_services || bundleRef?.bundle_services || [];
      const bundleName = joinedBundleData?.bundle_name || bundleRef?.bundle_name || userBundle.bundle_code;
      
      // Calculate retail value from bundle_services reference data
      let servicesRetail = 0;
      const serviceNames = [];
      
      if (bundleServices.length > 0) {
        // Use reference data for retail calculation
        bundleServices.forEach(bs => {
          const retailPrice = getServiceRetailPrice(bs.service_name);
          servicesRetail += retailPrice;
          serviceNames.push(bs.service_name);
          bundledServiceNames.add(bs.service_name.toLowerCase());
          
          // Add to retail breakdown
          retailBreakdown.push({
            name: bs.service_name,
            retail: retailPrice,
            source: `Included in ${bundleName}`,
            sourceType: 'bundle',
          });
        });
      } else {
        // Fallback: find services from subscriptions
        const subServices = subscriptions.filter(s => 
          s.payment_method === 'bundle' && s.payment_source === userBundle.bundle_code
        );
        subServices.forEach(s => {
          const retailPrice = parseFloat(s.retail_cost) || getServiceRetailPrice(s.service_name) || 0;
          servicesRetail += retailPrice;
          serviceNames.push(s.service_name);
          bundledServiceNames.add(s.service_name.toLowerCase());
          
          // Add to retail breakdown
          retailBreakdown.push({
            name: s.service_name,
            retail: retailPrice,
            source: `Included in ${bundleName}`,
            sourceType: 'bundle',
          });
        });
      }
      
      bundleRetailTotal += servicesRetail;
      
      bundleDetails.push({
        name: bundleName,
        code: userBundle.bundle_code,
        cost,
        retailTotal: servicesRetail,
        savings: servicesRetail - cost,
        creditApplied,
        actualCost,
        paymentMethod: userBundle.payment_method,
        paymentSource: userBundle.payment_source,
        services: serviceNames,
      });
    });
    
    const bundleSavings = bundleRetailTotal - bundleCost;

    // 1. Total retail value of all services (excluding bundled ones to avoid double-counting)
    let totalRetailValue = bundleRetailTotal; // Start with bundle retail values
    
    subscriptions.forEach(s => {
      // Skip services that are in bundles (already counted above)
      if (s.payment_method === 'bundle') return;
      if (bundledServiceNames.has(s.service_name?.toLowerCase())) return;
      
      const retail = parseFloat(s.retail_cost) || parseFloat(s.monthly_cost) || getServiceRetailPrice(s.service_name) || 0;
      totalRetailValue += retail;
      
      // Add to retail breakdown based on payment method
      let source = '';
      let sourceType = 'paid';
      
      if (s.payment_method === 'isp_perk' || s.payment_method === 'isp_bundle') {
        source = `Free via ${profile?.internetProvider || 'ISP'}`;
        sourceType = 'isp';
      } else if (s.payment_method === 'membership_perk' || s.payment_method === 'membership_benefit' || s.payment_method === 'nested_perk') {
        const membership = memberships?.find(m => m.membership_type === s.payment_source);
        source = `Free via ${membership?.membership_name || s.payment_source || 'Membership'}`;
        sourceType = 'membership';
      } else if (parseFloat(s.credit_applied) > 0) {
        const credit = parseFloat(s.credit_applied) || 0;
        const cost = parseFloat(s.monthly_cost) || 0;
        if (credit >= cost) {
          source = 'Covered by card credit';
        } else {
          source = `$${(cost - credit).toFixed(2)}/mo after credit`;
        }
        sourceType = 'card_credit';
      } else {
        const cost = parseFloat(s.monthly_cost) || 0;
        source = cost > 0 ? `You pay $${cost.toFixed(2)}/mo` : 'Cost not set';
        sourceType = 'paid';
      }
      
      retailBreakdown.push({
        name: s.service_name,
        retail,
        source,
        sourceType,
      });
    });

    // 3. ISP Perk value
    const ispPerkServices = subscriptions.filter(s => 
      (s.payment_method === 'isp_perk' || s.payment_method === 'isp_bundle') &&
      !bundledServiceNames.has(s.service_name?.toLowerCase())
    );
    const ispPerkValue = ispPerkServices.reduce((sum, s) => {
      return sum + (parseFloat(s.retail_cost) || parseFloat(s.monthly_cost) || getServiceRetailPrice(s.service_name) || 0);
    }, 0);
    const ispName = profile?.internetProvider || 'ISP';

    // 4. Membership perk value
    const membershipPerkServices = subscriptions.filter(s => 
      (s.payment_method === 'membership_perk' || s.payment_method === 'membership_benefit' || s.payment_method === 'nested_perk') &&
      !bundledServiceNames.has(s.service_name?.toLowerCase())
    );
    
    let membershipPerkValue = 0;
    let membershipCostForStreaming = 0;
    const membershipDetails = [];
    
    // Track which memberships are free via credit card perks
    const knownCardMembershipPerks = {
      'walmart_plus': ['american', 'amex', 'platinum'],
      'walmart+': ['american', 'amex', 'platinum'],
    };
    
    const cardFundedMemberships = new Map();
    
    (memberships || []).forEach(m => {
      const membershipType = (m.membership_type || '').toLowerCase().replace(/[\s-]/g, '_');
      const membershipName = (m.membership_name || '').toLowerCase().replace(/[\s-]/g, '_');
      
      const cardKeywords = knownCardMembershipPerks[membershipType] || 
                          knownCardMembershipPerks[membershipName] || [];
      
      if (cardKeywords.length > 0) {
        const matchingCard = (creditCards || []).find(card => {
          const cardNameLower = (card.card_name || '').toLowerCase();
          return cardKeywords.some(kw => cardNameLower.includes(kw));
        });
        
        if (matchingCard) {
          cardFundedMemberships.set(membershipType, matchingCard.card_name);
          cardFundedMemberships.set(membershipName, matchingCard.card_name);
        }
      }
    });
    
    membershipPerkServices.forEach(service => {
      const membership = memberships.find(m => m.membership_type === service.payment_source);
      const serviceRetail = parseFloat(service.retail_cost) || parseFloat(service.monthly_cost) || getServiceRetailPrice(service.service_name) || 0;
      
      if (membership) {
        const usesOtherBenefits = membership.uses_non_streaming_benefits !== false;
        const membershipType = (membership.membership_type || '').toLowerCase().replace(/[\s-]/g, '_');
        const membershipName = (membership.membership_name || '').toLowerCase().replace(/[\s-]/g, '_');
        const cardSource = cardFundedMemberships.get(membershipType) || cardFundedMemberships.get(membershipName);
        
        if (usesOtherBenefits) {
          membershipPerkValue += serviceRetail;
          membershipDetails.push({
            service: service.service_name,
            membership: membership.membership_name,
            value: serviceRetail,
            isFree: true,
            cardFunded: cardSource || null,
          });
        } else {
          membershipCostForStreaming += parseFloat(membership.monthly_cost) || 0;
          membershipDetails.push({
            service: service.service_name,
            membership: membership.membership_name,
            membershipCost: membership.monthly_cost,
            isFree: false,
            cardFunded: cardSource || null,
          });
        }
      }
    });

    // 5. Card credits applied - check BOTH subscriptions AND bundles AND allocations table
    const serviceCreditsApplied = subscriptions.reduce((sum, s) => {
      return sum + (parseFloat(s.credit_applied) || 0);
    }, 0);
    
    // Total credits from allocations table (more reliable source)
    const allocationsTotal = (allocations || []).reduce((sum, a) => {
      return sum + (parseFloat(a.amount_allocated) || 0);
    }, 0);
    
    // Use the higher of: direct credit_applied fields OR allocations table
    const totalCreditsApplied = Math.max(
      bundleCreditApplied + serviceCreditsApplied,
      allocationsTotal
    );
    
    const totalCreditsAvailable = creditCards.reduce((sum, c) => {
      return sum + (parseFloat(c.streaming_credit_monthly) || 0);
    }, 0);
    
    const unusedCredits = Math.max(0, totalCreditsAvailable - totalCreditsApplied);
    
    console.log('Credits calculation:', {
      allocations: allocations?.length || 0,
      allocationsTotal,
      bundleCreditApplied,
      serviceCreditsApplied,
      totalCreditsApplied,
      totalCreditsAvailable,
      unusedCredits,
      creditCards: creditCards?.map(c => ({ name: c.card_name, credit: c.streaming_credit_monthly })),
    });
    
    // Credit card breakdown - use allocations table for accuracy
    const cardCreditsDetails = creditCards
      .filter(c => parseFloat(c.streaming_credit_monthly) > 0)
      .map(card => {
        // Get allocations for this card
        const cardAllocations = (allocations || []).filter(a => a.credit_card_id === card.id);
        
        const allocatedServices = cardAllocations
          .filter(a => a.subscription_id && a.target_type !== 'bundle')
          .map(a => {
            const sub = subscriptions.find(s => s.id === a.subscription_id);
            return {
              name: sub?.service_name || 'Unknown',
              creditApplied: parseFloat(a.amount_allocated) || 0,
              cost: parseFloat(sub?.monthly_cost) || 0,
            };
          });
        
        const allocatedBundles = cardAllocations
          .filter(a => a.bundle_id || a.target_type === 'bundle')
          .map(a => {
            const bundle = bundles?.find(b => b.id === a.bundle_id);
            const bundleRef = refData?.bundles?.find(rb => rb.bundle_code === bundle?.bundle_code);
            return {
              name: bundle?.bundles?.bundle_name || bundleRef?.bundle_name || bundle?.bundle_code || 'Unknown',
              creditApplied: parseFloat(a.amount_allocated) || 0,
              cost: parseFloat(bundle?.monthly_cost) || 0,
            };
          });
        
        const servicesCreditUsed = allocatedServices.reduce((sum, s) => sum + s.creditApplied, 0);
        const bundlesCreditUsed = allocatedBundles.reduce((sum, b) => sum + b.creditApplied, 0);
        const totalUsed = servicesCreditUsed + bundlesCreditUsed;
        
        return {
          name: card.card_name,
          monthlyCredit: parseFloat(card.streaming_credit_monthly) || 0,
          used: totalUsed,
          remaining: Math.max(0, (parseFloat(card.streaming_credit_monthly) || 0) - totalUsed),
          services: allocatedServices.filter(s => s.creditApplied > 0),
          bundles: allocatedBundles.filter(b => b.creditApplied > 0),
        };
      });

    // 6. Direct pay total (non-bundled services paid directly)
    // Also check allocations table for any credits applied
    const directPayServices = subscriptions.filter(s => 
      (s.payment_method === 'paid' || s.payment_method === 'direct') &&
      !bundledServiceNames.has(s.service_name?.toLowerCase())
    );
    
    const directPayTotal = directPayServices.reduce((sum, s) => {
      const cost = parseFloat(s.monthly_cost) || parseFloat(s.actual_cost) || 0;
      // Check allocations table for credits applied to this subscription
      const subAllocations = (allocations || []).filter(a => a.subscription_id === s.id);
      const allocatedCredit = subAllocations.reduce((total, a) => 
        total + (parseFloat(a.amount_allocated) || 0), 0
      );
      // Use allocation credit if available, otherwise use stored credit_applied
      const creditApplied = allocatedCredit > 0 ? allocatedCredit : (parseFloat(s.credit_applied) || 0);
      return sum + Math.max(0, cost - creditApplied);
    }, 0);

    // 7. Partial credit (what's left after credit applied) - for services with card_credit payment method
    const partialCreditServices = subscriptions.filter(s => 
      (s.payment_method === 'card_credit' || s.payment_method === 'credit_card_perk' || s.payment_method === 'card_perk') &&
      !bundledServiceNames.has(s.service_name?.toLowerCase())
    );
    const partialCreditTotal = partialCreditServices.reduce((sum, s) => {
      const cost = parseFloat(s.monthly_cost) || 0;
      // Check allocations table for credits
      const subAllocations = (allocations || []).filter(a => a.subscription_id === s.id);
      const allocatedCredit = subAllocations.reduce((total, a) => 
        total + (parseFloat(a.amount_allocated) || 0), 0
      );
      const creditApplied = allocatedCredit > 0 ? allocatedCredit : (parseFloat(s.credit_applied) || 0);
      return sum + Math.max(0, cost - creditApplied);
    }, 0);

    // 8. Bundle actual cost (after credits applied to bundles)
    // Use allocations table as source of truth for credits applied to bundles
    const bundleActualCost = (bundles || []).reduce((sum, b) => {
      const monthlyCost = parseFloat(b.monthly_cost) || 0;
      
      // Check allocations table for credits applied to this bundle
      const bundleAllocations = (allocations || []).filter(a => 
        a.bundle_id === b.id || 
        (a.target_type === 'bundle' && a.bundle_id === b.id)
      );
      const allocatedCredit = bundleAllocations.reduce((total, a) => 
        total + (parseFloat(a.amount_allocated) || 0), 0
      );
      
      // Use allocation credit if available, otherwise fall back to stored credit_applied
      const creditApplied = allocatedCredit > 0 ? allocatedCredit : (parseFloat(b.credit_applied) || 0);
      
      // Calculate actual cost: what you pay = monthly cost - credit
      const calculatedActual = Math.max(0, monthlyCost - creditApplied);
      
      return sum + calculatedActual;
    }, 0);
    
    console.log('Bundle costs:', {
      bundles: (bundles || []).map(b => {
        const bundleAllocations = (allocations || []).filter(a => 
          a.bundle_id === b.id || (a.target_type === 'bundle' && a.bundle_id === b.id)
        );
        const allocatedCredit = bundleAllocations.reduce((total, a) => 
          total + (parseFloat(a.amount_allocated) || 0), 0
        );
        return {
          code: b.bundle_code,
          monthly_cost: b.monthly_cost,
          credit_applied_stored: b.credit_applied,
          credit_from_allocations: allocatedCredit,
          actual_cost_stored: b.actual_cost,
          calculated: Math.max(0, (parseFloat(b.monthly_cost) || 0) - (allocatedCredit || parseFloat(b.credit_applied) || 0))
        };
      }),
      bundleActualCost,
      allocations: allocations?.filter(a => a.bundle_id || a.target_type === 'bundle')
    });

    // 9. Final calculations
    console.log('Final calculation breakdown:', {
      bundleActualCost,
      directPayTotal,
      directPayServices: directPayServices.map(s => ({ name: s.service_name, cost: s.monthly_cost })),
      partialCreditTotal,
      partialCreditServices: partialCreditServices.map(s => ({ name: s.service_name, cost: s.monthly_cost, credit: s.credit_applied })),
      membershipCostForStreaming,
      totalCreditsApplied,
    });
    
    const totalOutOfPocket = bundleActualCost + directPayTotal + partialCreditTotal + membershipCostForStreaming;
    const totalSavings = totalRetailValue - totalOutOfPocket;
    const savingsPercent = totalRetailValue > 0 ? Math.round((totalSavings / totalRetailValue) * 100) : 0;

    // Waterfall math verification
    const waterfallDeductions = bundleSavings + ispPerkValue + membershipPerkValue + totalCreditsApplied;
    const waterfallResult = totalRetailValue - waterfallDeductions;
    console.log('Waterfall math check:', {
      totalRetailValue,
      bundleSavings,
      ispPerkValue, 
      membershipPerkValue,
      totalCreditsApplied,
      waterfallDeductions,
      waterfallResult,
      actualOutOfPocket: totalOutOfPocket,
      difference: waterfallResult - totalOutOfPocket,
    });

    // Count total services (unique)
    const allServiceNames = new Set([
      ...bundledServiceNames,
      ...subscriptions.map(s => s.service_name?.toLowerCase()).filter(Boolean)
    ]);
    const serviceCount = allServiceNames.size;

    return {
      totalRetailValue,
      retailBreakdown,
      bundleSavings,
      bundleCost,
      bundleCreditApplied,
      bundleActualCost,
      bundleDetails,
      ispPerkValue,
      ispPerkServices,
      ispName,
      membershipPerkValue,
      membershipDetails,
      membershipCostForStreaming,
      totalCreditsApplied,
      totalCreditsAvailable,
      unusedCredits,
      cardCreditsDetails,
      directPayTotal,
      directPayServices,
      partialCreditTotal,
      totalOutOfPocket,
      totalSavings,
      savingsPercent,
      serviceCount,
    };
  }, [subscriptions, creditCards, memberships, bundles, allocations, refData, profile]);

  const {
    totalRetailValue,
    retailBreakdown,
    bundleSavings,
    bundleCost,
    bundleDetails,
    ispPerkValue,
    ispPerkServices,
    ispName,
    membershipPerkValue,
    membershipDetails,
    totalCreditsApplied,
    cardCreditsDetails,
    unusedCredits,
    directPayTotal,
    partialCreditTotal,
    totalOutOfPocket,
    totalSavings,
    savingsPercent,
    serviceCount,
  } = calculations;

  return (
    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-5 text-white shadow-xl">
      
      {/* Header - Clickable to expand/collapse */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Your Streaming Value</h2>
        </div>
        <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-5 h-5" />
        </div>
      </button>
      
      {isExpanded ? (
        <div className="space-y-3">
          
          {/* Starting point - Retail value (NOW EXPANDABLE) */}
          <div className="bg-white/10 rounded-lg p-4">
            <button
              onClick={() => setShowRetailBreakdown(!showRetailBreakdown)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <div className={`transform transition-transform ${showRetailBreakdown ? 'rotate-90' : ''}`}>
                  <ChevronRight className="w-4 h-4 text-purple-300" />
                </div>
                <div className="text-left">
                  <p className="text-purple-200 text-sm">If you paid full retail price...</p>
                  <p className="text-xs text-purple-300 mt-0.5">
                    {serviceCount} service{serviceCount !== 1 ? 's' : ''} at regular prices
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold">${totalRetailValue.toFixed(2)}</span>
            </button>
            
            {/* Retail Breakdown (expandable) */}
            {showRetailBreakdown && (
              <div className="mt-3 pt-3 border-t border-white/20 space-y-2">
                {retailBreakdown.length > 0 ? (
                  retailBreakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Tv className="w-3 h-3 text-purple-300 flex-shrink-0" />
                        <span className="text-purple-100 truncate">{item.name}</span>
                        <span className="text-xs text-purple-400 truncate">({item.source})</span>
                      </div>
                      <span className="text-purple-200 flex-shrink-0 ml-2">${item.retail.toFixed(2)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-purple-300 text-center py-2">No services tracked yet</p>
                )}
                <div className="pt-2 border-t border-white/10 flex justify-between font-medium">
                  <span className="text-purple-200">Total retail value</span>
                  <span className="text-white">${totalRetailValue.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Bundle Savings */}
          {bundleSavings > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 flex justify-center">
                <ArrowDownCircle className="w-4 h-4 text-green-400" />
              </div>
              <div className="flex-1 bg-green-500/20 border border-green-400/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-400" />
                    <span className="font-medium">Bundle savings</span>
                  </div>
                  <span className="text-green-400 font-bold">-${bundleSavings.toFixed(2)}</span>
                </div>
                {bundleDetails.map((bundle, idx) => (
                  <p key={idx} className="text-xs text-green-200 mt-1 ml-6">
                    {bundle.name}: {bundle.services.length > 0 ? bundle.services.join(' + ') : 'Services'} 
                    {bundle.retailTotal > 0 && ` ($${bundle.retailTotal.toFixed(2)})`} → ${bundle.cost.toFixed(2)}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* Bundle Cost (when no savings but still show what they pay) */}
          {bundleSavings <= 0 && bundleCost > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 flex justify-center">
                <Package className="w-4 h-4 text-purple-300" />
              </div>
              <div className="flex-1 bg-white/10 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-300" />
                    <span className="font-medium">Bundles</span>
                  </div>
                  <span className="font-bold">${bundleCost.toFixed(2)}</span>
                </div>
                {bundleDetails.map((bundle, idx) => (
                  <p key={idx} className="text-xs text-purple-200 mt-1 ml-6">
                    {bundle.name}: {bundle.services.join(' + ')}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* ISP Perks */}
          {ispPerkValue > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 flex justify-center">
                <ArrowDownCircle className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-1 bg-blue-500/20 border border-blue-400/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-blue-400" />
                    <span className="font-medium">Free from ISP</span>
                  </div>
                  <span className="text-blue-400 font-bold">-${ispPerkValue.toFixed(2)}</span>
                </div>
                <p className="text-xs text-blue-200 mt-1 ml-6">
                  {ispPerkServices.map(s => s.service_name).join(', ')} via {ispName}
                </p>
              </div>
            </div>
          )}
          
          {/* Membership Perks */}
          {membershipPerkValue > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 flex justify-center">
                <ArrowDownCircle className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1 bg-amber-500/20 border border-amber-400/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-400" />
                    <span className="font-medium">Free from memberships</span>
                  </div>
                  <span className="text-amber-400 font-bold">-${membershipPerkValue.toFixed(2)}</span>
                </div>
                {membershipDetails.filter(d => d.isFree).map((detail, idx) => (
                  <p key={idx} className="text-xs text-amber-200 mt-1 ml-6">
                    {detail.service} via {detail.membership}
                    {detail.cardFunded ? (
                      <span className="text-amber-300 ml-1">(free with {detail.cardFunded})</span>
                    ) : (
                      <span className="text-amber-300 ml-1">(you use other benefits)</span>
                    )}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* Card Credits */}
          {totalCreditsApplied > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 flex justify-center">
                <ArrowDownCircle className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1 bg-emerald-500/20 border border-emerald-400/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-emerald-400" />
                    <span className="font-medium">Card credits applied</span>
                  </div>
                  <span className="text-emerald-400 font-bold">-${totalCreditsApplied.toFixed(2)}</span>
                </div>
                {cardCreditsDetails.filter(c => c.used > 0).map((card, idx) => {
                  const creditItems = [];
                  // Add bundles first
                  if (card.bundles && card.bundles.length > 0) {
                    card.bundles.forEach(b => {
                      creditItems.push(`${b.name} ($${b.creditApplied.toFixed(2)})`);
                    });
                  }
                  // Add services
                  card.services.forEach(s => {
                    creditItems.push(`${s.name} ($${s.creditApplied.toFixed(2)})`);
                  });
                  return (
                    <p key={idx} className="text-xs text-emerald-200 mt-1 ml-6">
                      {card.name}: {creditItems.length > 0 ? creditItems.join(' + ') : `$${card.used.toFixed(2)} applied`}
                    </p>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Final Cost */}
          <div className="border-t-2 border-white/20 pt-4 mt-4">
            <div className="bg-white/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">Your cost</p>
                  {totalSavings > 0 && (
                    <p className="text-sm text-purple-200">
                      Saving ${totalSavings.toFixed(2)}/mo ({savingsPercent}%)
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold">${totalOutOfPocket.toFixed(2)}</span>
                  <span className="text-purple-200 text-sm">/mo</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Unused Credits Warning */}
          {unusedCredits > 0 && (
            <div className="bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-yellow-400">⚠️</span>
                <span className="text-sm">
                  <span className="font-semibold">${unusedCredits.toFixed(2)}</span> in unused credits
                </span>
              </div>
              <button 
                onClick={onOptimizeClick}
                className="text-sm text-yellow-400 hover:text-yellow-300 font-medium"
              >
                Use it →
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Collapsed view - just show final cost */
        <div className="bg-white/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm">
                {serviceCount} service{serviceCount !== 1 ? 's' : ''} • Saving {savingsPercent}%
              </p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold">${totalOutOfPocket.toFixed(2)}</span>
              <span className="text-purple-200 text-sm">/mo</span>
            </div>
          </div>
          {unusedCredits > 0 && (
            <p className="text-yellow-400 text-sm mt-2">
              ⚠️ ${unusedCredits.toFixed(2)} unused credit available
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default WaterfallSummary;